import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";

/**
 * useContextMenu — shared behaviour for the reporter's portal context menus
 * (ChipContextMenu / ImpressionContextMenu / SectionContextMenu). One home for
 * the contract so the three menus can't drift:
 *
 *  - Positioning: clamp to the viewport on ALL four edges (8px floor), using
 *    the menu's MEASURED height (item count varies 1–5) rather than a
 *    hard-coded estimate. Runs in a layout effect so the menu never paints
 *    off-screen.
 *  - Dismissal: outside mousedown / contextmenu, Escape, and scroll/resize all
 *    close (the menu is fixed to screen coords, so a scroll would otherwise
 *    leave it pinned to stale anchor coords).
 *  - Keyboard a11y: focus the first enabled item on open, Arrow/Home/End roving
 *    focus, and restore focus to the trigger on Escape. Activating an item is
 *    NOT a restore (the action handler owns where focus lands — e.g. Dismiss
 *    refocuses the editor); only a no-choice keyboard dismissal returns focus.
 *
 * Returns the ref to spread onto the menu container and the positioned style.
 */
export function useContextMenu(
  anchor: { x: number; y: number },
  onClose: () => void,
  menuWidth: number,
): { ref: RefObject<HTMLDivElement | null>; style: CSSProperties } {
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const [style, setStyle] = useState<CSSProperties>(() => ({
    left: Math.max(8, Math.min(anchor.x, window.innerWidth - menuWidth - 8)),
    top: Math.max(8, anchor.y),
    width: menuWidth,
  }));

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const height = el.offsetHeight;
    setStyle({
      left: Math.max(8, Math.min(anchor.x, window.innerWidth - menuWidth - 8)),
      top: Math.max(8, Math.min(anchor.y, window.innerHeight - height - 8)),
      width: menuWidth,
    });
  }, [anchor.x, anchor.y, menuWidth]);

  // Focus the first enabled item on open; snapshot the trigger to restore on
  // Escape. Mount-only so a re-render never yanks focus back to the first item
  // mid-roving.
  useEffect(() => {
    triggerRef.current = document.activeElement;
    const first = ref.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)');
    first?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = ref.current;
    const items = (): HTMLElement[] =>
      el ? Array.from(el.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)')) : [];

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        const trigger = triggerRef.current;
        if (trigger instanceof HTMLElement && document.contains(trigger)) trigger.focus();
        onClose();
        return;
      }
      const list = items();
      if (list.length === 0) return;
      const idx = list.indexOf(document.activeElement as HTMLElement);
      if (e.key === "ArrowDown") {
        e.preventDefault();
        list[idx < 0 ? 0 : (idx + 1) % list.length]?.focus();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        list[idx < 0 ? list.length - 1 : (idx - 1 + list.length) % list.length]?.focus();
      } else if (e.key === "Home") {
        e.preventDefault();
        list[0]?.focus();
      } else if (e.key === "End") {
        e.preventDefault();
        list[list.length - 1]?.focus();
      }
    }
    function onPointer(e: MouseEvent) {
      if (el && !el.contains(e.target as Node)) onClose();
    }
    function onScrollResize() {
      onClose();
    }

    document.addEventListener("mousedown", onPointer);
    document.addEventListener("contextmenu", onPointer);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScrollResize, true);
    window.addEventListener("resize", onScrollResize);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("contextmenu", onPointer);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScrollResize, true);
      window.removeEventListener("resize", onScrollResize);
    };
  }, [onClose]);

  return { ref, style };
}
